/**
 * Workflow Start — Upfront Input Form, Hinglish + AI auto-fill
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Loader2, ArrowLeft, Wand2 } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import "./workflows.css";

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function WorkflowStart() {
  const { trackKey } = useParams();
  const navigate = useNavigate();
  const [track, setTrack] = useState(null);
  const [values, setValues] = useState({});
  const [seed, setSeed] = useState("");
  const [autofilling, setAutofilling] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/workflows/tracks/${trackKey}`)
      .then((r) => {
        setTrack(r.data);
        const defaults = {};
        (r.data.input_fields || []).forEach((f) => {
          if (f.default) defaults[f.name] = f.default;
        });
        setValues(defaults);
      })
      .catch(() => setError("Workflow not found"))
      .finally(() => setLoading(false));
  }, [trackKey]);

  const set = (name) => (e) => {
    const v = e.target.type === "number" ? Number(e.target.value) : e.target.value;
    setValues((prev) => ({ ...prev, [name]: v }));
  };

  const autofill = async () => {
    if (!seed.trim()) { toast.error("Apna idea ek line mein type karo"); return; }
    setAutofilling(true);
    try {
      const res = await axios.post(`${API}/api/workflows/autofill`, { track_key: trackKey, seed });
      const filled = res.data?.values || {};
      if (Object.keys(filled).length === 0) {
        toast.error("Auto-fill nahi ho saka — try a different line");
      } else {
        setValues((prev) => ({ ...prev, ...filled }));
        toast.success(`${Object.keys(filled).length} fields filled — review and submit`);
      }
    } catch (err) {
      toast.error("Auto-fill failed");
    } finally {
      setAutofilling(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API}/api/workflows/start`,
        { track_key: trackKey, input: values },
        { withCredentials: true }
      );
      const pid = res.data.project_id;
      navigate(`/workflows/project/${pid}`, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || "Could not start workflow");
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="wf-hub"><div className="wf-hub-inner" style={{ paddingTop: 120 }}>
          <Loader2 className="animate-spin" /> Loading…
        </div></div>
      </>
    );
  }
  if (error && !track) {
    return (
      <>
        <Header />
        <div className="wf-hub"><div className="wf-hub-inner">
          <p>{error}</p>
          <button className="wf-btn wf-btn--ghost" onClick={() => navigate("/workflows")}>
            <ArrowLeft size={16}/> Back to workflows
          </button>
        </div></div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="wf-hub" data-testid="workflow-start-page">
        <div className="wf-hub-inner" style={{ maxWidth: 760 }}>
          <button className="wf-btn wf-btn--ghost" onClick={() => navigate("/workflows")} data-testid="workflow-start-back" style={{ marginBottom: 28 }}>
            <ArrowLeft size={14}/> All workflows
          </button>
          <span className="wf-eyebrow">{track.tagline}</span>
          <h1 className="wf-hub-title" data-testid="workflow-start-title">{track.name}</h1>
          <p className="wf-hub-sub" style={{ marginBottom: 32 }}>
            Goal ko ek line mein bolo — niche AI saari fields fill kar dega.
            Ya manually fill karo. {track.steps.length} tools background mein chalenge.
          </p>

          {/* AI AUTO-FILL ROW */}
          <div className="wf-autofill-row" data-testid="wf-autofill-row">
            <Wand2 size={20} style={{ color: "#7c2d12", flexShrink: 0 }}/>
            <input
              type="text"
              value={seed}
              onChange={(e) => setSeed(e.target.value)}
              placeholder="Apna idea ek line mein… e.g. 'AI tools for freelance designers'"
              data-testid="wf-autofill-seed"
            />
            <button type="button" className="wf-autofill-btn" disabled={autofilling || !seed.trim()} onClick={autofill} data-testid="wf-autofill-btn">
              {autofilling ? <><Loader2 size={14} className="animate-spin"/> Filling…</> : <><Sparkles size={14}/> AI se fill karo</>}
            </button>
          </div>

          <form onSubmit={submit} data-testid="workflow-start-form">
            {(track.input_fields || []).map((f) => (
              <div key={f.name} style={{ marginBottom: 22 }}>
                <label style={{ display: "block", fontWeight: 600, marginBottom: 8, fontSize: "0.95rem" }}>
                  {f.label}{f.required ? <span style={{ color: "#dc2626" }}> *</span> : null}
                </label>
                {f.type === "textarea" ? (
                  <textarea
                    value={values[f.name] || ""}
                    onChange={set(f.name)}
                    placeholder={f.placeholder || ""}
                    required={f.required}
                    rows={3}
                    data-testid={`wf-input-${f.name}`}
                    style={{ width: "100%", padding: "12px 14px", borderRadius: 10, border: "1px solid rgba(109,40,217,0.18)", fontFamily: "inherit", fontSize: "0.95rem", resize: "vertical", background: "white" }}
                  />
                ) : f.type === "select" ? (
                  <select
                    value={values[f.name] || ""}
                    onChange={set(f.name)}
                    required={f.required}
                    data-testid={`wf-input-${f.name}`}
                    style={{ width: "100%", padding: "12px 14px", borderRadius: 10, border: "1px solid rgba(109,40,217,0.18)", background: "white", fontSize: "0.95rem" }}
                  >
                    <option value="">Choose…</option>
                    {(f.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                ) : (
                  <input
                    type={f.type === "number" ? "number" : f.type === "url" ? "url" : "text"}
                    value={values[f.name] || ""}
                    onChange={set(f.name)}
                    placeholder={f.placeholder || ""}
                    required={f.required}
                    data-testid={`wf-input-${f.name}`}
                    style={{ width: "100%", padding: "12px 14px", borderRadius: 10, border: "1px solid rgba(109,40,217,0.18)", fontSize: "0.95rem", background: "white" }}
                  />
                )}
              </div>
            ))}

            {error && (
              <p style={{ color: "#dc2626", marginBottom: 16, fontSize: "0.9rem" }} data-testid="workflow-start-error">{error}</p>
            )}

            <button type="submit" className="wf-btn" disabled={submitting} data-testid="workflow-start-submit">
              {submitting ? <><Loader2 size={16} className="animate-spin"/> Starting…</> : <><Sparkles size={16}/> Generate my package</>}
            </button>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
}
